import { useEffect, useMemo, useState } from 'react';
import { ArrowRight, Sparkles, Lightbulb } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useMonthlySummary } from '@/hooks/useMonthlySummary';
import { useTransactions } from '@/hooks/useTransactions';
import {
  getFocoScoreZone,
  transactionCategories,
  formatPercentage,
  type FocoScoreZone,
} from '@/lib/foco-calculations';
import { getWowMessage } from '@/lib/onboarding';
import { cn } from '@/lib/utils';

const EXPENSE_TYPES = ['fixed_expense', 'variable_expense', 'debt_payment'];

function categoryLabel(value: string): string {
  for (const group of Object.values(transactionCategories)) {
    const found = group.find((c) => c.value === value);
    if (found) return found.label;
  }
  return 'gastos';
}

const zoneAccent: Record<FocoScoreZone, string> = {
  critical: 'text-destructive',
  risk: 'text-warning',
  advance: 'text-success',
  liberty: 'text-accent',
};

interface OnboardingWowProps {
  open: boolean;
  year: number;
  month: number;
  providedScore?: number;
  onFinish: (score: number) => void;
}

export function OnboardingWow({ open, year, month, providedScore, onFinish }: OnboardingWowProps) {
  const { summary } = useMonthlySummary(year, month);
  const { transactions } = useTransactions(year, month);

  const score = providedScore ?? summary?.foco_score ?? 0;
  const zone = getFocoScoreZone(score);
  const { title, message } = getWowMessage(score);

  const [animated, setAnimated] = useState(0);

  useEffect(() => {
    if (!open) return;
    const duration = 1500;
    const steps = 60;
    const increment = score / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= score) {
        setAnimated(score);
        clearInterval(timer);
      } else {
        setAnimated(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [open, score]);

  const insight = useMemo(() => {
    const byCategory = new Map<string, number>();
    let totalExpenses = 0;
    for (const t of transactions ?? []) {
      if (!EXPENSE_TYPES.includes(t.type)) continue;
      const amt = Number(t.amount);
      if (!Number.isFinite(amt) || amt <= 0) continue;
      totalExpenses += amt;
      byCategory.set(t.category, (byCategory.get(t.category) ?? 0) + amt);
    }
    if (totalExpenses <= 0 || byCategory.size === 0) return null;
    let topCat = '';
    let topAmt = -1;
    for (const [cat, amt] of byCategory) {
      if (amt > topAmt) {
        topCat = cat;
        topAmt = amt;
      }
    }
    return {
      label: categoryLabel(topCat),
      pct: topAmt / totalExpenses,
    };
  }, [transactions]);

  return (
    <Dialog open={open}>
      <DialogContent
        className="sm:max-w-md gap-5 text-center"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        hideCloseButton
      >
        <DialogHeader className="items-center">
          <div className="rounded-full bg-primary/10 p-3">
            <Sparkles className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-lg">{title}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col items-center gap-1">
          <span className={cn('text-6xl font-bold tabular-nums', zoneAccent[zone])}>{animated}</span>
          <span className="text-xs text-muted-foreground">de 100</span>
        </div>

        <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>

        {insight && (
          <div className="flex items-start gap-2 rounded-lg border bg-muted/40 p-3 text-left">
            <Lightbulb className="h-4 w-4 text-warning shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Tu mayor oportunidad este mes:{' '}
              <strong className="text-foreground">{insight.label}</strong> representa el{' '}
              <strong className="text-foreground">{formatPercentage(insight.pct)}</strong> de tus egresos.
            </p>
          </div>
        )}

        <Button type="button" className="w-full gap-2" onClick={() => onFinish(score)}>
          Ver mi dashboard
          <ArrowRight className="h-4 w-4" />
        </Button>
      </DialogContent>
    </Dialog>
  );
}
